import { useEffect, useRef, useState } from 'preact/hooks';
import { useParams } from 'react-router-dom';
import type { RatedProductWire } from 'shared';
import { ApiError, getApiClient } from '@/lib/api-client.js';
import { buildCheckoutPayload } from '@/lib/build-checkout-payload.js';
import type { Customer } from '@/lib/widget-types.js';
import {
  applyRateResponse,
  cart,
  cartItemCount,
  customer,
  quoteId,
  ratedProducts,
  resetRatingSession,
  unavailableProviders,
  vehicle,
} from '@/state/session.js';
import { findCartLine } from '@/state/pricing.js';
import {
  customerFormValidation,
  resetCustomerForm,
  showCustomerErrors,
} from '@/state/customer-form.js';
import { resetPaymentForm, showPaymentErrors } from '@/flows/outreach/state/payment-form.js';
import { sleep, useTranscript } from '@/flows/outreach/use-transcript.js';
import {
  groupByProduct,
  groupsNotInCart,
  type RatedProductGroup,
} from '@/lib/group-rated-products.js';
import { FullscreenLayer } from '@/flows/outreach/components/fullscreen-layer.js';
import { WidgetLoading } from '@/components/widget-loading.js';
import { ChatBubble } from '@/flows/outreach/components/chat-bubble.js';
import { OutreachCart } from '@/flows/outreach/components/chat-cart.js';
import { ChatError } from '@/flows/outreach/components/chat-error.js';
import { Composer, type ComposerSpec } from '@/flows/outreach/components/composer.js';
import {
  checkoutErrorCopy,
  checkoutPayloadErrorCopy,
  rateErrorCopy,
  seedErrorCopy,
  seedErrorKind,
  toLine,
  type SeedErrorKind,
} from '@/lib/error-copy.js';
import { OutreachProductCard } from '@/flows/outreach/components/product-card.js';
import { OutreachCustomerInfoCard } from '@/flows/outreach/components/customer-info-card.js';
import { OutreachPaymentCard } from '@/flows/outreach/components/payment-card.js';
import { OutreachSummaryCard } from '@/flows/outreach/components/summary-card.js';
import { TypingIndicator } from '@/flows/outreach/components/typing-indicator.js';
import { DeadEndAttachment } from '@/flows/outreach/components/dead-end-attachment.js';
import {
  dealerCopy,
  getScriptFor,
  type ContinueStep,
  type SeedType,
  type StepResult,
} from '@/flows/outreach/scripts.js';

/** Lets the "connecting" line stay readable before the browser navigates away. */
const CHECKOUT_HANDOFF_MS = 400;

/** `tiers → product-payment → add-another` loops until the customer stops adding plans. */
type Phase =
  | 'delivering'
  | 'awaiting'
  | 'rating'
  | 'tiers'
  | 'product-payment'
  | 'add-another'
  | 'customer-info'
  | 'summary'
  | 'submitting'
  | 'dead-end';

interface OutreachFlowProps {
  /** False on our hosted page, where dismissing would leave a blank tab. `FullscreenLayer` drops
      the close button when given no `onClose`, so withholding the handler is the whole of it. */
  dismissible: boolean;
}

export function OutreachFlow({ dismissible }: OutreachFlowProps) {
  const params = useParams();
  const outreachId = params.outreachId ?? '';
  const { status, errorKind, retry, seedType } = useOutreachSeed(outreachId);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const ready = status !== 'loading' && status !== 'error' && seedType !== null;

  return (
    <FullscreenLayer
      onClose={dismissible ? () => setDismissed(true) : undefined}
      headerAction={ready ? <OutreachCart /> : undefined}
    >
      {status === 'loading' ? (
        <WidgetLoading label="Starting your conversation…" />
      ) : !ready ? (
        <ChatError
          message={seedErrorCopy(errorKind).message}
          onRetry={seedErrorCopy(errorKind).retryable ? retry : undefined}
        />
      ) : (
        <OutreachChatSession seedType={seedType} outreachId={outreachId} />
      )}
    </FullscreenLayer>
  );
}

interface OutreachChatSessionProps {
  seedType: SeedType;
  outreachId: string;
}

function OutreachChatSession({ seedType, outreachId }: OutreachChatSessionProps) {
  // Returns one of two module-level arrays, so the reference is already stable enough
  // to sit in the delivery effect's dep list.
  const script = getScriptFor(seedType);
  const { messages, scrollRef, say, sayNow, echo, show, pending } = useTranscript();

  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('delivering');
  // Rate and checkout failures can't coexist — each phase reaches only one of them —
  // so they share one line below the thread.
  const [error, setError] = useState<string | null>(null);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  /** Null once this plan's payment is confirmed. */
  const [activeCartIndex, setActiveCartIndex] = useState<number | null>(null);
  // Cards are delivered more than once — the opening set, then the leftovers each time the
  // customer adds another — and only the newest batch stays live. `0` is none live.
  const cardBatchRef = useRef(0);
  const [liveCardBatch, setLiveCardBatch] = useState(0);

  // Every customer action runs through `advance`, and the composer stays unmounted for
  // its duration — so the bottom bar is never live while the dealer is "typing". The ref
  // does the double-tap guarding (synchronous, unlike a state write); the state unmounts.
  // Validation stays outside it, or a rejected submit would flicker the bar.
  const advancingRef = useRef(false);
  const [advancing, setAdvancing] = useState(false);

  async function advance(run: () => void | Promise<void>): Promise<void> {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setAdvancing(true);
    try {
      await run();
    } finally {
      advancingRef.current = false;
      setAdvancing(false);
    }
  }

  useEffect(() => {
    if (phase !== 'delivering') return;
    const stepDef = script[stepIndex];
    if (!stepDef) return;

    let cancelled = false;
    const isFirstStep = stepIndex === 0;

    void (async () => {
      const lines: Array<() => string> = [...stepDef.bubbles];
      if (stepDef.kind === 'yes-no' && stepDef.prompt) {
        const { prompt } = stepDef;
        lines.push(() => prompt);
      }

      for (const [index, toText] of lines.entries()) {
        if (cancelled) return;
        // The very first line of the conversation lands immediately; every later one
        // shows a typing indicator first.
        if (isFirstStep && index === 0) sayNow(toText());
        else await say(toText());
      }

      if (!cancelled) setPhase('awaiting');
    })();

    return () => {
      cancelled = true;
    };
  }, [phase, stepIndex, script, say, sayNow]);

  const currentStep = script[stepIndex];

  async function applyResult(result: StepResult): Promise<void> {
    if (result.kind === 'next') {
      setStepIndex((i) => i + 1);
      setPhase('delivering');
      return;
    }
    if (result.kind === 'rate') {
      await runRate();
      return;
    }
    await runDeadEnd(result.copy);
  }

  async function runDeadEnd(copy: string): Promise<void> {
    setPhase('dead-end');
    await say(copy);
    await show({ kind: 'attachment' });
  }

  async function runRate({ retry = false }: { retry?: boolean } = {}): Promise<void> {
    const currentVehicle = vehicle.value;
    if (currentVehicle === null) {
      // The seed always carries a vehicle, so this is unreachable today — but leaving
      // the phase alone would strand the customer in `awaiting` with a retry that can
      // never succeed. A dead end at least ends with the dealer's contact card.
      await runDeadEnd(dealerCopy.lostVehicle);
      return;
    }
    setPhase('rating');
    setError(null);

    // Acknowledged immediately rather than after the usual typing pause: the request
    // is already in flight, and the composer is gone until it lands.
    sayNow(dealerCopy.rateAck(retry));
    const clearTyping = pending();

    try {
      const res = await getApiClient().rate({
        flow_type: seedType,
        vehicle: {
          vin: currentVehicle.vin,
          mileage: currentVehicle.mileage,
          year: currentVehicle.year ?? undefined,
          make: currentVehicle.make ?? undefined,
          model: currentVehicle.model ?? undefined,
        },
        outreach_id: outreachId,
      });
      // A fresh outreach quote — the chat has no prior cart to preserve.
      applyRateResponse(res, 'clear');
      clearTyping();
      await deliverTiers(res.rated_products);
    } catch (err) {
      setError(toLine(rateErrorCopy(err)));
      clearTyping();
      setPhase('awaiting');
    }
  }

  // Called on the tap, not on the phase flip that follows: the dealer's reply is several
  // typing beats away, and cards that settle only then settle long after the tap did.
  function retireCards(): void {
    setLiveCardBatch(0);
  }

  /** Phase flips before the first card, not after the last, so each card lands live. */
  async function showProductCards(groups: RatedProductGroup[]): Promise<void> {
    cardBatchRef.current += 1;
    const batch = cardBatchRef.current;
    setLiveCardBatch(batch);
    setPhase('tiers');
    for (const group of groups) {
      await show({ kind: 'product-card', quoteItemIds: group.quoteItemIds, batch });
    }
  }

  async function deliverTiers(rated: RatedProductWire[]): Promise<void> {
    const groups = groupByProduct(rated);

    if (groups.length === 0) {
      await runDeadEnd(dealerCopy.noPlans(unavailableProviders.value.length > 0));
      return;
    }

    await say(dealerCopy.tiersIntro(seedType));
    await showProductCards(groups);
  }

  async function handlePlanSelected(cartIndex: number): Promise<void> {
    const line = findCartLine(cartIndex);
    if (line === undefined) return;
    echo(`I'll take ${line.tier.product_name}`);
    retireCards();
    setActiveCartIndex(cartIndex);
    // Errors revealed on the previous plan shouldn't greet them on this one.
    resetPaymentForm();
    await say(dealerCopy.paymentPrompt(line.tier.product_name));
    await show({ kind: 'payment-card', cartIndex });
    setPhase('product-payment');
  }

  async function handlePaymentConfirmed(cartIndex: number): Promise<void> {
    const line = findCartLine(cartIndex);
    if (line === undefined) return;
    echo('Confirm');
    setActiveCartIndex(null);
    await say(dealerCopy.planConfirmed(line.tier.product_name));

    // Everything on offer is already configured — nothing left to branch to.
    if (groupsNotInCart().length === 0) {
      await say(dealerCopy.noMoreProducts);
      await handleCheckoutTap();
      return;
    }

    await say(dealerCopy.addAnotherPrompt);
    setPhase('add-another');
  }

  async function handleAddAnother(): Promise<void> {
    echo('Yes, add another');
    await say(dealerCopy.backToTiers);
    // The opening cards are far up a scrolled thread by now — "pick another plan below"
    // has to actually put plans below.
    await showProductCards(groupsNotInCart());
  }

  async function handleCheckoutTap(): Promise<void> {
    retireCards();
    await say(dealerCopy.checkoutIntro);
    await show({ kind: 'customer-info-card' });
    setPhase('customer-info');
  }

  async function handleCustomerInfoContinue(validated: Customer): Promise<void> {
    customer.value = validated;
    echo('Looks good');
    await say(dealerCopy.summaryIntro);
    await show({ kind: 'summary-card' });
    setPhase('summary');
  }

  async function runCheckout(): Promise<void> {
    // Retry after a failed checkout: the previous "Go to checkout" echo is still in
    // the thread, so don't stack an identical one on every attempt. Mirrors how the
    // rate path avoids re-echoing on retry.
    if (error === null) echo('Go to checkout');
    setPhase('submitting');
    setError(null);

    // The only signal for this wait — no typing indicator, and the composer is unmounted.
    const clearConnecting = pending(dealerCopy.connecting);

    /** Every recoverable failure lands back on the summary with the composer live. */
    function failBackToSummary(line: string): void {
      setError(line);
      clearConnecting();
      setPhase('summary');
    }

    const customerValue = customer.value;
    const vehicleValue = vehicle.value;
    const quoteIdValue = quoteId.value;
    if (customerValue === null || vehicleValue === null || quoteIdValue === null) {
      failBackToSummary(dealerCopy.missingSessionDetails);
      return;
    }

    const built = buildCheckoutPayload({
      quoteId: quoteIdValue,
      customer: customerValue,
      vehicle: vehicleValue,
      cart: cart.value,
      ratedProducts: ratedProducts.value,
    });

    if (!built.ok) {
      failBackToSummary(toLine(checkoutPayloadErrorCopy(built.reason)));
      return;
    }

    try {
      const res = await getApiClient().checkout(built.payload);
      await sleep(CHECKOUT_HANDOFF_MS);
      window.location.href = res.payment_link;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'QUOTE_ALREADY_CONVERTED') {
        // Terminal: the quote is already an order. Drop into a dead-end (no composer)
        // rather than re-enabling a "Go to checkout" that would just re-hit this.
        clearConnecting();
        await runDeadEnd(toLine(checkoutErrorCopy(err)));
        return;
      }
      failBackToSummary(toLine(checkoutErrorCopy(err)));
    }
  }

  async function runScriptContinue(step: ContinueStep): Promise<void> {
    const result = step.onComplete();
    // Retry path after a failed rate — don't double-echo the bubble.
    if (error !== null && result.kind === 'rate') {
      setError(null);
      await runRate({ retry: true });
      return;
    }
    echo(step.customerEcho());
    await applyResult(result);
  }

  // The composer's shape and what its primary button does are decided together, so a
  // new phase can't be added to one and missed by the other.
  function composerSpec(): ComposerSpec | null {
    if (phase === 'tiers') {
      // Tapping a card moves the flow on; this bar is only the way out. The echo lives
      // here rather than in `handleCheckoutTap`, which add-another's "No" also calls.
      return {
        kind: 'checkout',
        itemCount: cartItemCount.value,
        onCheckout: () =>
          void advance(async () => {
            echo('Continue to checkout');
            await handleCheckoutTap();
          }),
      };
    }
    if (phase === 'product-payment') {
      const cartIndex = activeCartIndex;
      if (cartIndex === null) return null;
      return {
        kind: 'continue',
        label: 'Confirm',
        onContinue: () => {
          // Only this plan's down payment gates the step — the others are already settled.
          if (findCartLine(cartIndex)?.downPaymentError != null) {
            showPaymentErrors.value = true;
            return;
          }
          void advance(() => handlePaymentConfirmed(cartIndex));
        },
      };
    }
    if (phase === 'add-another') {
      return {
        kind: 'yes-no',
        onYes: () => void advance(handleAddAnother),
        onNo: () =>
          void advance(async () => {
            echo('No, that’s everything');
            await handleCheckoutTap();
          }),
      };
    }
    if (phase === 'customer-info') {
      return {
        kind: 'continue',
        label: 'Looks good',
        onContinue: () => {
          const validated = customerFormValidation.value.customer;
          if (validated === null) {
            showCustomerErrors.value = true;
            return;
          }
          void advance(() => handleCustomerInfoContinue(validated));
        },
      };
    }
    if (phase === 'summary') {
      return {
        kind: 'continue',
        label: 'Go to checkout',
        onContinue: () => void advance(runCheckout),
      };
    }
    // `submitting` drops the composer, like `delivering` and `rating` — the wait is
    // announced in the transcript, so a locked button below it is just dead weight.
    if (phase !== 'awaiting') return null;
    if (!currentStep) return null;
    if (currentStep.kind === 'continue') {
      const step = currentStep;
      return {
        kind: 'continue',
        label: step.label,
        onContinue: () => void advance(() => runScriptContinue(step)),
      };
    }
    // Bind the narrowed step so each handler closes over the step it was built for,
    // rather than re-checking `currentStep.kind` at call time.
    if (currentStep.kind === 'yes-no') {
      const step = currentStep;
      return {
        kind: 'yes-no',
        onYes: () =>
          void advance(async () => {
            echo(step.customerEcho('yes'));
            await applyResult(step.onYes());
          }),
        onNo: () =>
          void advance(async () => {
            echo(step.customerEcho('no'));
            await applyResult(step.onNo());
          }),
      };
    }
    const step = currentStep;
    return {
      kind: 'input',
      inputMode: step.inputMode,
      validate: step.validate,
      placeholder: step.inputLabel,
      onSubmit: (value: string) =>
        void advance(async () => {
          echo(step.customerEcho(value));
          await applyResult(step.onSubmit(value));
        }),
    };
  }

  const composer = composerSpec();

  return (
    <div className="sm-stack sm-chat-shell">
      <div className="sm-stack sm-chat-scroll" ref={scrollRef}>
        <div className="sm-stack sm-chat-thread">
          {messages.map((m) => {
            if (m.kind === 'typing') return <TypingIndicator key={m.id} />;
            if (m.kind === 'attachment') return <DeadEndAttachment key={m.id} />;
            if (m.kind === 'product-card') {
              return (
                <OutreachProductCard
                  key={m.id}
                  quoteItemIds={m.quoteItemIds}
                  expanded={expandedCardId === m.id}
                  onToggleExpand={() => setExpandedCardId((prev) => (prev === m.id ? null : m.id))}
                  onSelected={(cartIndex) => void advance(() => handlePlanSelected(cartIndex))}
                  selectable={phase === 'tiers' && m.batch === liveCardBatch}
                />
              );
            }
            if (m.kind === 'customer-info-card') {
              return <OutreachCustomerInfoCard key={m.id} />;
            }
            if (m.kind === 'payment-card') {
              return (
                <OutreachPaymentCard
                  key={m.id}
                  cartIndex={m.cartIndex}
                  editable={phase === 'product-payment' && m.cartIndex === activeCartIndex}
                />
              );
            }
            if (m.kind === 'summary-card') {
              return <OutreachSummaryCard key={m.id} />;
            }
            return (
              <ChatBubble key={m.id} side={m.side}>
                {m.text}
              </ChatBubble>
            );
          })}
          {error ? (
            <div className="sm-type-body sm-danger-surface sm-chat-rate-error">{error}</div>
          ) : null}
        </div>
      </div>
      {composer && !advancing ? <Composer spec={composer} /> : null}
    </div>
  );
}

function useOutreachSeed(outreachId: string): {
  status: 'loading' | 'ready' | 'error';
  errorKind: SeedErrorKind;
  retry: () => void;
  seedType: SeedType | null;
} {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorKind, setErrorKind] = useState<SeedErrorKind>('unknown');
  const [seedType, setSeedType] = useState<SeedType | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    resetRatingSession();
    setStatus('loading');

    let cancelled = false;
    void (async () => {
      try {
        const seed = await getApiClient().outreachOpen(outreachId);
        if (cancelled) return;
        customer.value = {
          firstName: seed.customer.first_name,
          lastName: seed.customer.last_name,
          email: seed.customer.email ?? '',
          phone: seed.customer.phone,
          address: {
            street1: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US',
          },
        };
        vehicle.value = {
          vin: seed.vehicle.vin,
          mileage: seed.vehicle.mileage,
          year: seed.vehicle.year,
          make: seed.vehicle.make,
          model: seed.vehicle.model,
        };
        // Seed the checkout form from the customer we just loaded, and clear both
        // error-reveal flags in case this is a retry of a previous session.
        resetCustomerForm();
        resetPaymentForm();
        setSeedType(seed.type);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorKind(seedErrorKind(err));
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [outreachId, reloadNonce]);

  return {
    status,
    errorKind,
    retry: () => setReloadNonce((n) => n + 1),
    seedType,
  };
}
